import { z } from 'zod';
import type { OutcomeCategory } from './outcomes';

export const sourceTypeSchema = z.enum([
  'subject',
  'description',
  'latest_comment',
  'latest_public_comment',
  'custom_field',
]);

export const identityTypeSchema = z.enum(['email', 'phone']);
export const userCreationModeSchema = z.enum(['allow', 'deny']);
export const phoneDisplayNamePolicySchema = z.enum(['masked_last4']);

export const patternInputSchema = z.object({
  expression: z.string().trim().min(1).max(1_000),
  case_insensitive: z.boolean().default(true),
});

export const ruleInputSchema = z
  .object({
    name: z.string().trim().min(2).max(120),
    source_type: sourceTypeSchema,
    identity_type: identityTypeSchema.default('email'),
    custom_field_id: z.number().int().positive().nullable().optional(),
    phone_default_country: z.literal('NANP').nullable().optional(),
    user_creation_mode: userCreationModeSchema.default('allow'),
    phone_display_name_policy: phoneDisplayNamePolicySchema.nullable().optional(),
    active: z.boolean().default(true),
    patterns: z.array(patternInputSchema).min(1).max(20),
  })
  .superRefine((rule, context) => {
    if (rule.source_type === 'custom_field' && !rule.custom_field_id) {
      context.addIssue({
        code: 'custom',
        message: 'A text custom field is required for this source.',
        path: ['custom_field_id'],
      });
    }
    if (rule.identity_type === 'phone' && rule.phone_default_country !== 'NANP') {
      context.addIssue({
        code: 'custom',
        message: 'Phone rules require an explicit NANP normalization policy.',
        path: ['phone_default_country'],
      });
    }
    if (rule.identity_type === 'phone' && rule.user_creation_mode === 'allow' && !rule.phone_display_name_policy) {
      context.addIssue({
        code: 'custom',
        message: 'Phone user creation requires an approved display-name policy.',
        path: ['phone_display_name_policy'],
      });
    }
  });

export const resolveInputSchema = z.object({
  ticket_id: z.number().int().positive(),
  rule_key: z.string().regex(/^rule_[A-Za-z0-9_-]{20,80}$/),
});

export const sampleTestInputSchema = z.object({
  sample: z.string().max(20_000),
  identity_type: identityTypeSchema.default('email'),
  phone_default_country: z.literal('NANP').nullable().optional(),
  patterns: z.array(patternInputSchema).min(1).max(20),
});

export const liveTicketTestInputSchema = z.object({
  ticket_id: z.number().int().positive(),
});

export type SourceType = z.infer<typeof sourceTypeSchema>;
export type IdentityType = z.infer<typeof identityTypeSchema>;
export type UserCreationMode = z.infer<typeof userCreationModeSchema>;
export type PhoneDisplayNamePolicy = z.infer<typeof phoneDisplayNamePolicySchema>;
export type PatternInput = z.infer<typeof patternInputSchema>;
export type RuleInput = z.infer<typeof ruleInputSchema>;

export interface RulePattern extends PatternInput {
  id: string;
  position: number;
}

export interface RuleSummary {
  guards?: Array<{ kind: 'email' | 'phone_handoff' | 'description'; settings: Record<string, string | number | null> }>;
  id: string;
  rule_key: string;
  name: string;
  source_type: SourceType;
  identity_type: IdentityType;
  version: number;
  custom_field_id: number | null;
  phone_default_country: 'NANP' | null;
  user_creation_mode: UserCreationMode;
  phone_display_name_policy: PhoneDisplayNamePolicy | null;
  active: boolean;
  patterns: RulePattern[];
  created_at: string;
  updated_at: string;
}

export type ResolutionStatus =
  | 'would_create'
  | 'resolved'
  | 'created'
  | 'unchanged'
  | 'no_match'
  | 'disabled_rule'
  | 'validation_failed'
  | 'invalid_phone'
  | 'ambiguous_identity'
  | 'suspended_user'
  | 'user_creation_disabled'
  | 'out_of_scope'
  | 'identity_reference_missing'
  | 'identity_reference_mismatch'
  | 'user_creation_conflict';

export interface ResolveResponse {
  resolved: boolean;
  status: ResolutionStatus;
  requester_id?: number;
  execution_id: string;
}

export interface ExecutionRow {
  id: string;
  ticket_id: number;
  status: string;
  masked_email: string | null;
  masked_phone: string | null;
  user_created: number;
  error_code: string | null;
  created_at: string;
  updated_at: string;
  rule_version: number;
  verification_status: string | null;
  ingress_type: string;
  category: OutcomeCategory;
}
